/**
 * Linear multi-pattern matching for runtime redaction patterns.
 *
 * @internal Used by the redactor. Not a supported public API.
 */
/** Number of distinct UTF-16 code unit values, used to pack an edge key. */
const UNIT_RANGE = 0x10000;
const isHighSurrogate = (unit) => unit >= 0xd800 && unit <= 0xdbff;
const isLowSurrogate = (unit) => unit >= 0xdc00 && unit <= 0xdfff;
/**
 * Creates an Aho-Corasick matcher. Matching runs in time linear in the text
 * length, independent of the number of patterns, with constant stack use.
 * Registering a pattern makes the next {@link PatternMatcher.redact} rebuild
 * the failure links once, which costs up to the total length of the registered
 * patterns, more than the trie size when patterns share long prefixes.
 *
 * @example
 * const matcher = createPatternMatcher('[REDACTED]');
 * matcher.add('AB', {kind: 'replace', replacement: '[AB]'});
 * matcher.add('BC', {kind: 'replace', replacement: '[BC]'});
 * matcher.redact('xABCx'); // {status: 'redacted', text: 'x[REDACTED]x'}
 *
 * @param defaultReplacement - The non-empty mask for merged spans and for
 * replace patterns registered with an empty mask.
 * @throws RangeError when `defaultReplacement` is empty.
 * @internal
 */
export const createPatternMatcher = (defaultReplacement) => {
    if (defaultReplacement === '') {
        throw new RangeError('defaultReplacement must not be empty');
    }
    const root = {
        id: 0,
        parent: undefined,
        unit: 0,
        depth: 0,
        fail: undefined,
        replacement: undefined,
        detect: false,
        replaceOutput: undefined,
        detectOutput: undefined,
    };
    // One shared map keeps per-node memory small: key = node id and code unit.
    const edges = new Map();
    // levels[d] holds the nodes at depth d + 1, which is the order the build needs.
    const levels = [];
    let nodeCount = 0;
    let dirty = false;
    const edgeKey = (node, unit) => node.id * UNIT_RANGE + unit;
    const failOf = (node) => node.fail ?? root;
    const add = (pattern, registration) => {
        if (pattern === '') {
            return;
        }
        let node = root;
        for (let index = 0; index < pattern.length; index++) {
            const unit = pattern.charCodeAt(index);
            let next = edges.get(edgeKey(node, unit));
            if (next === undefined) {
                next = {
                    id: ++nodeCount,
                    parent: node,
                    unit,
                    depth: node.depth + 1,
                    fail: root,
                    replacement: undefined,
                    detect: false,
                    replaceOutput: undefined,
                    detectOutput: undefined,
                };
                edges.set(edgeKey(node, unit), next);
                const level = levels[node.depth] ?? [];
                level.push(next);
                levels[node.depth] = level;
            }
            node = next;
        }
        if (node.replacement !== undefined || node.detect) {
            return;
        }
        switch (registration.kind) {
            case 'replace':
                node.replacement =
                    registration.replacement || defaultReplacement;
                break;
            case 'detect':
                node.detect = true;
                break;
            default: {
                const unhandled = registration;
                return unhandled;
            }
        }
        dirty = true;
    };
    const findFail = (node, parent) => {
        for (let candidate = failOf(parent);; candidate = failOf(candidate)) {
            const target = edges.get(edgeKey(candidate, node.unit));
            if (target !== undefined) {
                return target;
            }
            if (candidate === root) {
                return root;
            }
        }
    };
    // Breadth-first by depth, so every fail target is already final.
    const build = () => {
        for (const level of levels) {
            for (const node of level) {
                node.fail =
                    node.parent === root || node.parent === undefined
                        ? root
                        : findFail(node, node.parent);
                const fail = failOf(node);
                node.replaceOutput =
                    node.replacement !== undefined ? node : fail.replaceOutput;
                node.detectOutput = node.detect ? node : fail.detectOutput;
            }
        }
        dirty = false;
    };
    /**
     * Whether every detection lies inside one replaced span. Both lists are
     * ordered by end, and spans are disjoint, so the only span that can contain
     * a detection is the first one ending at or after it.
     */
    const allCovered = (detections, spans) => {
        let spanIndex = 0;
        for (const detection of detections) {
            let span = spans[spanIndex];
            while (span !== undefined && span.end < detection.end) {
                spanIndex++;
                span = spans[spanIndex];
            }
            if (span === undefined || span.start > detection.start) {
                return false;
            }
        }
        return true;
    };
    const redact = text => {
        if (nodeCount === 0 || text === '') {
            return { status: 'redacted', text };
        }
        if (dirty) {
            build();
        }
        const spans = [];
        const detections = [];
        let state = root;
        for (let index = 0; index < text.length; index++) {
            const unit = text.charCodeAt(index);
            for (;;) {
                const next = edges.get(edgeKey(state, unit));
                if (next !== undefined) {
                    state = next;
                    break;
                }
                if (state === root) {
                    break;
                }
                state = failOf(state);
            }
            const end = index + 1;
            const detected = state.detectOutput;
            if (detected !== undefined) {
                detections.push({ start: end - detected.depth, end });
            }
            const match = state.replaceOutput;
            if (match?.replacement === undefined) {
                continue;
            }
            let start = end - match.depth;
            let spanEnd = end;
            let exact = true;
            if (start > 0 &&
                isLowSurrogate(text.charCodeAt(start)) &&
                isHighSurrogate(text.charCodeAt(start - 1))) {
                start--;
                exact = false;
            }
            if (spanEnd < text.length &&
                isHighSurrogate(text.charCodeAt(spanEnd - 1)) &&
                isLowSurrogate(text.charCodeAt(spanEnd))) {
                spanEnd++;
                exact = false;
            }
            // This span ends at or after every earlier one, so it can only
            // overlap the spans on top of the stack.
            let earliest = start;
            for (let top = spans.at(-1); top && top.end > start; top = spans.at(-1)) {
                earliest = Math.min(earliest, top.start);
                spans.pop();
            }
            spans.push({
                start: earliest,
                end: spanEnd,
                replacement: exact && earliest === start
                    ? match.replacement
                    : defaultReplacement,
            });
        }
        if (!allCovered(detections, spans)) {
            return { status: 'contains-detect-only' };
        }
        let result = '';
        let copied = 0;
        for (const span of spans) {
            result += text.slice(copied, span.start) + span.replacement;
            copied = span.end;
        }
        return { status: 'redacted', text: result + text.slice(copied) };
    };
    return { add, redact };
};
//# sourceMappingURL=patternMatcher.js.map